import { useState, useEffect, useCallback } from 'react';

type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'debug-theme-mode';

const COLOR_MAP: Record<'light' | 'dark', Record<string, string>> = {
  light: {
    '--dbg-bg': '#ffffff',
    '--dbg-bg-secondary': '#f8fafc',
    '--dbg-bg-code': '#f1f5f9',
    '--dbg-text': '#0f172a',
    '--dbg-text-secondary': '#64748b',
    '--dbg-text-muted': '#475569',
    '--dbg-text-dim': '#94a3b8',
    '--dbg-border': '#e2e8f0',
    '--dbg-border-input': '#cbd5e1',
    '--dbg-accent': '#2563eb',
    '--dbg-accent-bg': '#dbeafe',
    '--dbg-hover': '#f1f5f9',
    '--dbg-switch': '#2563eb',
  },
  dark: {
    '--dbg-bg': '#0f172a',
    '--dbg-bg-secondary': '#1e293b',
    '--dbg-bg-code': '#020617',
    '--dbg-text': '#f1f5f9',
    '--dbg-text-secondary': '#94a3b8',
    '--dbg-text-muted': '#cbd5e1',
    '--dbg-text-dim': '#64748b',
    '--dbg-border': '#334155',
    '--dbg-border-input': '#475569',
    '--dbg-accent': '#60a5fa',
    '--dbg-accent-bg': '#1d4ed8',
    '--dbg-hover': '#334155',
    '--dbg-switch': '#3b82f6',
  },
};

export const useDebugTheme = () => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored as ThemeMode) || 'system';
  });

  const [isDark, setIsDark] = useState(false);

  // Compute isDark based on themeMode
  useEffect(() => {
    const computeIsDark = () => {
      if (themeMode === 'light') {
        return false;
      }
      if (themeMode === 'dark') {
        return true;
      }
      // themeMode === 'system'
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    };

    setIsDark(computeIsDark());

    // Add listener for system preference changes (only when mode is 'system')
    if (themeMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        setIsDark(e.matches);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
  }, [themeMode]);

  // Apply CSS variables to document.documentElement
  useEffect(() => {
    const colors = COLOR_MAP[isDark ? 'dark' : 'light'];

    Object.entries(colors).forEach(([varName, value]) => {
      document.documentElement.style.setProperty(varName, value);
    });
  }, [isDark]);

  // Persist themeMode to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, themeMode);
  }, [themeMode]);

  const handleSetThemeMode = useCallback((mode: ThemeMode) => {
    setThemeMode(mode);
  }, []);

  const cssVars = COLOR_MAP[isDark ? 'dark' : 'light'];

  return {
    isDark,
    themeMode,
    setThemeMode: handleSetThemeMode,
    cssVars,
  };
};
